import React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import useTicketMatrixData from '../../../hooks/useTicketMatrixData';
import { generateClientId, VIATOR_AGE_BANDS } from './viatorProducts';

/**
 * Build the "Viator inventory" by hand: each product gets a Viator-facing name,
 * one ticket type, and a per-age-band map to a ticket option with a custom net
 * price. State lives directly on `values.settings.viatorProducts` (cents), saved
 * through the integration form's single editIntegration call.
 */
const ViatorProductsEditor = ({ showId, values, setFieldValue }) => {
    const { t } = useTranslation();
    const { ticketTypes, ticketOptions, loading, error } = useTicketMatrixData({ showId });

    const products = values.settings?.viatorProducts || [];

    const updateProducts = (next) => {
        setFieldValue('settings', { ...values.settings, viatorProducts: next });
    };

    const patchProduct = (index, patch) => {
        updateProducts(
            products.map((product, i) => (i === index ? { ...product, ...patch } : product)),
        );
    };

    const addProduct = () => {
        // No code yet: it is derived from the name and assigned on save (then
        // frozen). `_id` is a transient React key, stripped before persisting.
        updateProducts([
            ...products,
            { _id: generateClientId(), name: '', ticketTypeId: '', ageBands: {} },
        ]);
    };

    const removeProduct = (index) => {
        updateProducts(products.filter((_, i) => i !== index));
    };

    const setBand = (index, band, row) => {
        const ageBands = { ...(products[index].ageBands || {}) };
        if (row) {
            ageBands[band] = row;
        } else {
            delete ageBands[band];
        }
        patchProduct(index, { ageBands });
    };

    const handleOptionChange = (index, band, optionId) => {
        if (!optionId) {
            setBand(index, band, null);
            return;
        }
        const product = products[index];
        const existing = product.ageBands?.[band];
        const option = ticketOptions.find((opt) => opt.id === optionId);
        const ticketType = ticketTypes.find((type) => type.id === product.ticketTypeId);
        // Snapshot the ticket type's matrix price as a starting point, but copy
        // the value so later price-matrix edits never mutate the Viator product.
        const snapshot = option ? ticketType?.priceMatrix?.[option.name] : undefined;
        setBand(index, band, {
            ticketOptionId: optionId,
            price: existing?.price ?? snapshot ?? 0,
        });
    };

    const handlePriceChange = (index, band, value) => {
        const row = products[index].ageBands?.[band];
        if (!row) {
            return;
        }
        const cents = value === '' ? 0 : Math.round(parseFloat(value) * 100);
        setBand(index, band, { ...row, price: Number.isNaN(cents) ? 0 : cents });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{t('integrations.viatorProducts.errorLoading')}</Alert>;
    }

    const hasTickets = ticketTypes.length > 0 && ticketOptions.length > 0;

    return (
        <Box>
            <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ textAlign: 'left', fontWeight: 600 }}
            >
                {t('integrations.viatorProducts.title')}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
                {t('integrations.viatorProducts.info')}
            </Alert>

            {!hasTickets && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('integrations.viatorProducts.noTickets')}
                </Alert>
            )}

            <Stack spacing={2}>
                {products.map((product, index) => (
                    <Paper key={product._id || product.code} variant="outlined" sx={{ p: 2 }}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems="flex-start"
                        >
                            <TextField
                                fullWidth
                                label={t('integrations.viatorProducts.productName')}
                                value={product.name || ''}
                                onChange={(ev) => patchProduct(index, { name: ev.target.value })}
                            />
                            <TextField
                                select
                                sx={{ width: { xs: '100%', sm: 220 } }}
                                label={t('integrations.viatorProducts.ticketType')}
                                value={product.ticketTypeId || ''}
                                onChange={(ev) =>
                                    patchProduct(index, { ticketTypeId: ev.target.value })
                                }
                            >
                                {ticketTypes.map((type) => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <IconButton
                                aria-label={t('common.delete')}
                                onClick={() => removeProduct(index)}
                                sx={{ mt: 1 }}
                            >
                                <DeleteOutlineIcon />
                            </IconButton>
                        </Stack>

                        <Typography variant="caption" color="text.secondary">
                            {t('integrations.viatorProducts.code')}:{' '}
                            {product.code || (
                                <em>{t('integrations.viatorProducts.codeGeneratedOnSave')}</em>
                            )}
                        </Typography>

                        <TableContainer sx={{ mt: 1 }}>
                            <Table size="small" sx={{ minWidth: 420 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            {t('integrations.viatorProducts.ageBand')}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            {t('integrations.viatorProducts.option')}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: 150 }}>
                                            {t('integrations.viatorProducts.price')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {VIATOR_AGE_BANDS.map((band) => {
                                        const row = product.ageBands?.[band];
                                        const bandOptions = ticketOptions.filter(
                                            (opt) => opt.ageBand === band,
                                        );
                                        // Show the ticket type's matrix price for
                                        // reference and flag when the custom net
                                        // price overrides it.
                                        const selectedOption = row
                                            ? ticketOptions.find(
                                                  (opt) => opt.id === row.ticketOptionId,
                                              )
                                            : null;
                                        const selectedType = ticketTypes.find(
                                            (type) => type.id === product.ticketTypeId,
                                        );
                                        const originalCents = selectedOption
                                            ? selectedType?.priceMatrix?.[selectedOption.name]
                                            : undefined;
                                        const isOverridden =
                                            !!row &&
                                            originalCents !== undefined &&
                                            row.price !== originalCents;
                                        const priceHelper =
                                            row && originalCents !== undefined
                                                ? `${t('integrations.viatorProducts.originalPrice')}: $${(
                                                      originalCents / 100
                                                  ).toFixed(2)}${
                                                      isOverridden
                                                          ? ` (${t('integrations.viatorProducts.overridden')})`
                                                          : ''
                                                  }`
                                                : undefined;
                                        return (
                                            <TableRow key={band}>
                                                <TableCell>
                                                    {t(`ticketoptions.ageBand.${band}`)}
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        size="small"
                                                        disabled={
                                                            !product.ticketTypeId ||
                                                            bandOptions.length === 0
                                                        }
                                                        value={row?.ticketOptionId || ''}
                                                        onChange={(ev) =>
                                                            handleOptionChange(
                                                                index,
                                                                band,
                                                                ev.target.value,
                                                            )
                                                        }
                                                        SelectProps={{ displayEmpty: true }}
                                                    >
                                                        <MenuItem value="">
                                                            <em>
                                                                {t(
                                                                    'integrations.viatorProducts.notOffered',
                                                                )}
                                                            </em>
                                                        </MenuItem>
                                                        {bandOptions.map((option) => (
                                                            <MenuItem
                                                                key={option.id}
                                                                value={option.id}
                                                            >
                                                                {option.name}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="number"
                                                        disabled={!row}
                                                        inputProps={{
                                                            min: 0,
                                                            step: 0.01,
                                                            style: { textAlign: 'right' },
                                                        }}
                                                        value={
                                                            row ? (row.price / 100).toFixed(2) : ''
                                                        }
                                                        onChange={(ev) =>
                                                            handlePriceChange(
                                                                index,
                                                                band,
                                                                ev.target.value,
                                                            )
                                                        }
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    {row ? '$' : '-'}
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        helperText={priceHelper}
                                                        sx={
                                                            isOverridden
                                                                ? {
                                                                      '& .MuiFormHelperText-root': {
                                                                          color: 'warning.main',
                                                                      },
                                                                  }
                                                                : undefined
                                                        }
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                ))}
            </Stack>

            <Button
                startIcon={<AddIcon />}
                onClick={addProduct}
                sx={{ mt: 2 }}
                disabled={!hasTickets}
            >
                {t('integrations.viatorProducts.addProduct')}
            </Button>
        </Box>
    );
};

export default ViatorProductsEditor;
